/**
 * Internal Sales Search Component
 * 
 * ⚠️ WARNING: This component connects to INTERNAL backend endpoints with Basic Authentication.
 * All requests automatically include Basic Auth headers from sessionStorage.
 * Endpoint: /_dummy/test_test (GET, PDF, XLS)
 */

import React from "react";
import { Paper, Grid, TextField, MenuItem, Button } from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "app/store";
import { setInternalSalesFilters } from "app/main/sales/store/internal-filter-sales.slice";
import { fetchInternalSales } from "app/main/sales/store/internal-sales-list.slice";
import { SalesSource } from "app/types/service-types/SalesTypes";
import { useTranslation } from "react-i18next";
import { InternalSalesService } from "app/services/internal-sales.services";

const SOURCES: (SalesSource | "")[] = ["", "ADMIN", "WEB", "ANDROID", "IOS", "SKYSCANNER", "SKYSCANNER_MOB", "WEB_MANUAL", "BMT_NETWORK","BMT_NETWORK_EDREAMS", "BMT_NETWORK_BOOKING"];
const boolOpts = [{ label: "true", value: true }, { label: "false", value: false }];

const InternalSearchSaleTable: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const filters = useSelector((s: AppState) => s.internalSales.filters);

  const submit = () => dispatch(fetchInternalSales(filters));

  const download = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const onPdf = async () => {
    const resp = await InternalSalesService.exportPdf(filters);

    const filename =
      resp.headers["content-disposition"]?.split("filename=")?.[1]?.replace(/"/g, "") ||
      `internal_sales_${filters.fromDate}_${filters.toDate}.pdf`;

    const blob = new Blob([resp.data], { type: "application/pdf" });
    download(blob, filename);
  };

  const onXls = async () => {
    const resp = await InternalSalesService.exportXls(filters);
    const filename =
      resp.headers["content-disposition"]?.split("filename=")?.[1]?.replace(/"/g, "") ||
      `internal_sales_${filters.fromDate}_${filters.toDate}.xls`;
    download(resp.data, filename);
  };

  return (
    <Paper style={{ padding: 12, marginBottom: 12 }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={3}>
          <TextField
            fullWidth
            label={t("From date", "From date")}
            type="date"
            value={filters.fromDate}
            onChange={(e) => dispatch(setInternalSalesFilters({ fromDate: e.target.value }))}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField
            fullWidth
            label={t("To date", "To date")}
            type="date"
            value={filters.toDate}
            onChange={(e) => dispatch(setInternalSalesFilters({ toDate: e.target.value }))}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} sm={2}>
          <TextField
            select
            fullWidth
            label={t("Paid", "Paid")}
            value={String(filters.isPaid)}
            onChange={(e) => dispatch(setInternalSalesFilters({ isPaid: e.target.value === "true" }))}
          >
            {boolOpts.map((b) => (
              <MenuItem key={String(b.value)} value={String(b.value)}>
                {b.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={2}>
          <TextField
            select
            fullWidth
            label={t("Source", "Source")}
            value={filters.source ?? ""}
            onChange={(e) => dispatch(setInternalSalesFilters({ source: e.target.value as any }))}
          >
            {SOURCES.map((s) => (
              <MenuItem key={s || "all"} value={s}>
                {s || t("All", "All")}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={2}>
          <Button fullWidth variant="contained" color="primary" onClick={submit}>
            {t("Search", "Search")}
          </Button>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Button fullWidth variant="outlined" onClick={onPdf}>
            {t("Export PDF", "Export PDF")}
          </Button>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Button fullWidth variant="outlined" onClick={onXls}>
            {t("Export Excel", "Export Excel")}
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default InternalSearchSaleTable;
